import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kMatterBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { TransferMatterOwnershipResponseSchema, type TransferMatterOwnershipResponse } from '@/matter/store/schemas'
import { getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export async function postTransferMatterOwnership(matterId: string, newOwnerUserId: string): Promise<TransferMatterOwnershipResponse> {
  const transactionId = nanoid()
  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterBasePath}${matterId}/transfer-ownership`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': transactionId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ new_owner_user_id: newOwnerUserId }),
    })
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const detail = (data as { detail?: unknown } | undefined)?.detail
      if (![400, 403, 404, 409].includes(response.status)) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: transactionId })
          Sentry.captureException(new Error(`Transfer ownership failed - status: ${response.status}`), {
            extra: getResponseDebugContext(response, apiUrl),
          })
        })
      }
      throw new ApiError(response.status, `Transfer ownership failed - status: ${response.status}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error('Transfer ownership response JSON parse failed'), {
          extra: getResponseDebugContext(response, apiUrl),
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid transfer ownership response JSON')
    }

    const parsed = TransferMatterOwnershipResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error('Transfer ownership response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues.map(({ code, path }) => ({ code, path })),
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid transfer ownership response shape', parsed.error.issues)
    }
    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
